import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input, Select, message, Popconfirm, Spin,
    Card, Row, Col, Space, Divider, Typography, Dropdown, Menu, Avatar,
    Badge, Tag, Statistic, Tooltip, Popover, Progress,
    Alert
} from 'antd';
import {
    DownloadOutlined, PlusOutlined, ImportOutlined, ExportOutlined,
    QrcodeOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
    EyeOutlined, MoreOutlined, SyncOutlined, CheckCircleOutlined,
    CloseCircleOutlined, FileExcelOutlined, FilePdfOutlined, ShoppingOutlined,
    BarcodeOutlined, UploadOutlined, SettingOutlined, InfoCircleOutlined,
    StockOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import * as XLSX from 'xlsx';
import 'jspdf-autotable';
import exportToPDF from './reports/product_lists_pdf';
import { BACKENDURL, BACKENDURLFiles, getPermission } from '../../constants';

const { Title, Text } = Typography;
const { Option } = Select;

const userPermission = getPermission();

const typeList = [
    ...(userPermission?.is_positive_adjustment ? [{ id: 'positive', name: 'Positive' }] : []),
    ...(userPermission?.is_negative_adjustment ? [{ id: 'negative', name: 'Negative' }] : [])
];

// Format currency as Tanzanian Shillings
const formatCurrency = (value) => {
    return `Tsh ${parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const statusTag = (status) => {
    switch (status) {
        case 'active':
            return <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>;
        case 'inactive':
            return <Tag icon={<CloseCircleOutlined />} color="error">Inactive</Tag>;
        default:
            return <Tag icon={<SyncOutlined spin />} color="processing">Unknown</Tag>;
    }
};

const Products = () => {
    const [products, setProducts] = useState([]);
    const [shops, setShops] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [currentProductIndex, setCurrentProductIndex] = useState(null);
    const [searchParams, setSearchParams] = useState({ name: '', category_id: '', sub_category_id: '' });
    const [selectedFile, setSelectedFile] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [conflictShops, setConflictShops] = useState([]);
    const [confirming, setConfirming] = useState(false);
    const [units, setUnits] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [qrLoading, setQrLoading] = useState(false);

    useEffect(() => {
        fetchProducts();
        fetchShops();
        fetchCategories();
        fetchUnits();
    }, [searchParams]);

    const handleImport = async () => {
        if (!selectedFile) {
            message.warning('Please select a file first.');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        setLoading(true);

        try {
            const response = await axiosInstance.post('/import-products', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.confirm_required) {
                setConflictShops(response.data.conflict_shops || []);
                setShowConfirmModal(true);
            } else {
                message.success('Products imported successfully');
                fetchProducts();
                setImportModalVisible(false);
                setSelectedFile(null);
            }
        } catch (error) {
            handleImportError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleImportError = (error) => {
        console.log('Full error object:', error);
        console.log('Error response:', error.response);
        console.log('Error response data:', error.response?.data);

        // Check for import_results FIRST, regardless of status code
        if (error.response?.data?.import_results) {
            displayImportResults(error.response.data.import_results);
            return; // Important: stop here
        }

        // Then check for validation errors
        if (error.response?.status === 422 && error.response?.data?.errors) {
            const errorDetails = error.response.data.errors;
            for (const field in errorDetails) {
                message.error(`Error in "${field}": ${errorDetails[field].join(', ')}`);
            }
            return;
        }

        // Then check for general message
        if (error.response?.data?.message) {
            message.error(error.response.data.message);
            return;
        }

        // Default error message
        message.error('Failed to import products. Try again.');
        console.error(error);
    };

    const displayImportResults = (results) => {
        const {
            imported,
            skipped_duplicates,
            missing_categories,
            missing_subcategories,
            validation_errors
        } = results;

        // Show missing categories
        if (missing_categories && missing_categories.length > 0) {
            Modal.error({
                title: 'Missing Categories',
                width: 600,
                content: (
                    <div>
                        <p>The following categories don't exist. Please create them first:</p>
                        <ul>
                            {missing_categories.map((cat, idx) => (
                                <li key={idx}>
                                    <strong>{cat.name}</strong> (rows: {cat.rows.join(', ')})
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            });
        }

        // Show missing subcategories
        if (missing_subcategories && missing_subcategories.length > 0) {
            Modal.error({
                title: 'Missing Sub-Categories',
                width: 600,
                content: (
                    <div>
                        <p>The following sub-categories don't exist. Please create them first:</p>
                        <ul>
                            {missing_subcategories.map((sub, idx) => (
                                <li key={idx}>
                                    <strong>{sub.name}</strong> under category "{sub.category}" (rows: {sub.rows.join(', ')})
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            });
        }

        // Show validation errors
        if (validation_errors && validation_errors.length > 0) {
            Modal.error({
                title: 'Validation Errors',
                width: 700,
                content: (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <p>{validation_errors.length} row(s) had validation errors:</p>
                        <ul>
                            {validation_errors.map((err, idx) => (
                                <li key={idx}>
                                    <strong>Row {err.row}:</strong> {err.error}
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            });
        }

        // Show skipped duplicates as info
        if (skipped_duplicates && skipped_duplicates.length > 0) {
            Modal.info({
                title: 'Skipped Duplicates',
                width: 700,
                content: (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <p>{skipped_duplicates.length} duplicate product(s) were skipped:</p>
                        <ul>
                            {skipped_duplicates.map((dup, idx) => (
                                <li key={idx}>
                                    <strong>Row {dup.row}:</strong> {dup.name} ({dup.category}/{dup.subcategory})
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            });
        }

        // Show success summary
        if (imported > 0) {
            message.success(`Successfully imported ${imported} product(s)`);
        }
    };

    const fetchUnits = async () => {
        try {
            const response = await axiosInstance.get('/units');
            if (response.data) {
                setUnits(response.data);
            } else {
                message.error('Failed to fetch units.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch units.');
        }
    };

    const handleConfirmImport = async (value) => {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('confirm_shared', value ? '1' : '0');

        setConfirming(true);
        try {
            const response = await axiosInstance.post('/import-products', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Check if response has import_results (even on success)
            if (response.data?.import_results) {
                displayImportResults(response.data.import_results);
            } else {
                message.success(response.data.message || 'Products imported successfully');
            }

            fetchProducts();
            setImportModalVisible(false);
            setShowConfirmModal(false);
            setSelectedFile(null);
        } catch (error) {
            handleImportError(error);
        } finally {
            setConfirming(false);
        }
    };

    const handleFileClick = () => {
        document.getElementById('import-file-input').click();
    };

    const handleSearch = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleDownloadTemplate = (event) => {
        event.preventDefault();

        const data = [
            ['name', 'buying_price', 'selling_price', 'expire_date', 'category_name', 'sub_category_name', 'barcode', 'sku', 'unit_name'],
            ['Example Product', '10000', '15000', '2025-12-31', 'Electronics', 'Phones', '1234567890', 'SKU-001', 'pcs'],
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);
        const headerStyle = {
            font: { bold: true },
        };

        for (let col = 0; col < data[0].length; col++) {
            const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];
            if (cell) {
                cell.s = headerStyle;
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');

        const excelFile = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'binary'
        });

        const blob = new Blob([s2ab(excelFile)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'products.xlsx';
        link.click();
    };

    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) {
            view[i] = s.charCodeAt(i) & 0xFF;
        }
        return buf;
    };

    const handleCategoryChange = (value) => {
        setSearchParams(prevState => ({
            ...prevState,
            category_id: value,
        }));
        fetchSubCategories(value);
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            let url = '/products';
            const params = [];

            if (searchParams.name) {
                params.push(`name=${encodeURIComponent(searchParams.name)}`);
            }
            if (searchParams.category_id) {
                params.push(`category_id=${searchParams.category_id}`);
            }
            if (searchParams.sub_category_id) {
                params.push(`sub_category_id=${searchParams.sub_category_id}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await axiosInstance.get(url);
            if (response.data.products) {
                setProducts(response.data.products);
            } else {
                message.error('Failed to fetch products.');
                setProducts([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch products.');
        } finally {
            setLoading(false);
        }
    };

    const fetchShops = async () => {
        try {
            const response = await axiosInstance.get('/shops');
            if (response.data.shops) {
                setShops(response.data.shops);
            } else {
                message.error('Failed to fetch shops.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch shops.');
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories) {
                setCategories(response.data.categories);
            } else {
                message.error('Failed to fetch categories.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch categories.');
        }
    };

    const fetchSubCategories = async (categoryId) => {
        setSubCategories([]);
        try {
            const response = await axiosInstance.get(`/categories/${categoryId}/sub-categories`);
            if (response.data.subCategories) {
                setSubCategories(response.data.subCategories);
            } else {
                message.warning('No sub-categories found for this category.');
                setSubCategories([]);
            }
        } catch (error) {
            console.error('Error fetching sub-categories:', error);
            message.error('Failed to fetch sub-categories.');
            setSubCategories([]);
        }
    };

    const showModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            fetchSubCategories(product.category_id);
            form.setFieldsValue({
                name: product.name,
                buying_price: product.buying_price,
                selling_price: product.selling_price,
                quantity: product.quantity ?? 0,
                expire_date: product.expire_date,
                category_id: product.category.id,
                sub_category_id: product.sub_category?.id,
                unit_id: product.unit?.id,
                barcode: product.barcode,
                sku: product.sku,
            });
        } else {
            setEditingProduct(null);
            form.resetFields();
            form.setFieldsValue({ quantity: 0 });
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            const { quantity } = values;
            if (quantity < 0) {
                message.error('Quantity cannot be negative.');
                return;
            }

            const isEditing = !!editingProduct;
            const endpoint = isEditing ? `/products/${editingProduct.id}` : '/products/register';
            const method = isEditing ? axiosInstance.put : axiosInstance.post;

            const response = await method(endpoint, values);

            if (response.data.confirm_required) {
                const modalTitle = isEditing ? 'Confirm Shared Update' : 'Confirm Shared Registration';
                const modalContent = isEditing
                    ? 'This product exists in shared shops. Do you want to update it in all shared shops too?'
                    : 'This shop has shared shops. Do you want to register this product to all of them?';

                Modal.confirm({
                    title: modalTitle,
                    content: modalContent,
                    okText: isEditing ? 'Yes, Update All' : 'Yes, Register to All',
                    cancelText: isEditing ? 'No, Only This Shop' : 'No, Only Main Shop',
                    onOk: async () => {
                        try {
                            await method(endpoint, {
                                ...values,
                                confirm_shared: true,
                            });
                            message.success(isEditing
                                ? 'Product updated in all shared shops.'
                                : 'Product registered to all shared shops.');
                            fetchProducts();
                            setIsModalVisible(false);
                            form.resetFields();
                        } catch (err) {
                            message.error(isEditing
                                ? 'Failed to update shared shops.'
                                : 'Failed to register to shared shops.');
                        }
                    },
                    onCancel: async () => {
                        try {
                            await method(endpoint, {
                                ...values,
                                confirm_shared: false,
                            });
                            message.success(isEditing
                                ? 'Product updated only in the current shop.'
                                : 'Product registered only to the main shop.');
                            fetchProducts();
                            setIsModalVisible(false);
                            form.resetFields();
                        } catch (err) {
                            message.error(isEditing
                                ? 'Failed to update product.'
                                : 'Failed to register product.');
                        }
                    },
                });
                return;
            }

            message.success(isEditing ? 'Product updated successfully' : 'Product registered successfully');
            fetchProducts();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || 'Failed to save product.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/products/${id}`);
            message.success('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to delete product.');
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            await axiosInstance.patch(`/products/${id}/status`, { status });
            message.success('Product status updated successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to update product status.');
        }
    };

    const downloadImage = (imagePath, productName) => {
        const cleanPath = imagePath.split('?')[0];
        const proxyUrl = `${BACKENDURL}/storage/${cleanPath}`;

        axiosInstance.get(proxyUrl, {
            responseType: "blob",
        })
            .then(response => {
                if (response.status !== 200) throw new Error("Failed to fetch image");
                return response.data;
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const cleanProductName = productName
                    .replace(/[^a-z0-9]/gi, '_')
                    .toLowerCase();

                const fileExt = cleanPath.split('.').pop();
                a.download = `${cleanProductName}_barcode.${fileExt}`;

                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(error => console.error("Error downloading image:", error));
    };

    const exportToExcel = async () => {
        setExportLoading(true);
        try {
            const data = products.map((product, index) => ({
                Name: product.name,
                SKU: product.sku || '',
                'Buying Price': formatCurrency(product.buying_price),
                'Selling Price': formatCurrency(product.selling_price),
                Quantity: product.total_quantity,
                Category: product.category?.name || '',
                'Sub-Category': product.sub_category?.name || '',
                Barcode: product.barcode || '',
                Status: product.status?.status || '',
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Products');
            XLSX.writeFile(wb, 'products.xlsx');
            message.success('Products exported successfully!');
        } catch (error) {
            console.error("Error during export:", error.message);
            message.error('Failed to export products!');
        } finally {
            setExportLoading(false);
        }
    };

    const exportToPDFd = async () => {
        setExportLoading(true);
        try {
            exportToPDF(products);
            message.success('PDF generated successfully!');
        } catch (error) {
            console.error("Error during export:", error.message);
            message.error('Failed to export products!');
        } finally {
            setExportLoading(false);
        }
    };

    const qrImageGenrt = async () => {
        setQrLoading(true);
        try {
            const response = await axiosInstance.post('products/generate-qr-images');

            if (response.data && response.data.message) {
                message.success(response.data.message);
                window.location.reload();
            } else {
                message.success('QR images generated successfully!');
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Failed to generate images. Please try again.');
            }
        } finally {
            setQrLoading(false);
        }
    };

    const openModal = async (id) => {
        try {
            const response = await axiosInstance.get(`/sales/sales-prices/${id}`);
            if (response.data) {
                setSelectedProductDetails(response.data.details);
                setCurrentProductIndex(id);
                setModalVisible(true);
            }
        } catch (error) {
            console.error('Failed to fetch product details:', error);
        }
    };

    const getCategoryColor = (category) => {
        const colors = ['#f5222d', '#fa8c16', '#52c41a', '#1890ff', '#722ed1', '#13c2c2', '#eb2f96'];
        const index = category ? category.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const handleBatch = (id) => {
        window.location.href = `/batch-management/${id}`;
    }

    const columns = [
        {
            title: 'Product',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        style={{
                            backgroundColor: getCategoryColor(record.category?.name),
                            marginRight: 12,
                            color: '#fff'
                        }}
                    >
                        {text.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight: 500 }}>{text}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                            {record.barcode ? `Barcode: ${record.barcode}` : 'No barcode'}
                        </div>
                    </div>
                </div>
            ),
            fixed: 'left',
            width: 250
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
            render: (sku) => sku || <Text type="secondary">-</Text>,
            width: 130
        },
        {
            title: 'Buying Price',
            dataIndex: 'buying_price',
            key: 'buying_price',
            render: (price) => (
                <Text strong>{formatCurrency(price)}</Text>
            ),
            sorter: (a, b) => a.buying_price - b.buying_price,
            width: 150
        },
        {
            title: 'Selling Price',
            dataIndex: 'selling_price',
            key: 'selling_price',
            render: (price) => (
                <Text strong type="success">{formatCurrency(price)}</Text>
            ),
            sorter: (a, b) => a.selling_price - b.selling_price,
            width: 150
        },
        {
            title: 'Stock',
            key: 'quantity',
            render: (_, product) => {
                // Use actual data from backend
                const totalStock = product.total_quantity || 0;
                const availableStock = product.available_quantity || product.total_quantity || 0;
                const percent = totalStock > 0 ? Math.round((availableStock / totalStock) * 100) : 0;

                return (
                    <div>
                        <div style={{ marginBottom: 4 }}>
                            <Text strong>{availableStock}</Text>
                            <Text type="secondary"> / {totalStock}</Text>
                        </div>
                        <Progress
                            percent={percent}
                            showInfo={false}
                            strokeColor={percent > 70 ? '#52c41a' : percent > 30 ? '#faad14' : '#f5222d'}
                            strokeWidth={8}
                        />
                    </div>
                );
            },
            width: 180
        },
        {
            title: 'Category',
            dataIndex: ['category', 'name'],
            key: 'category',
            render: (text) => (
                <Tag color={getCategoryColor(text)}>
                    {text || 'Uncategorized'}
                </Tag>
            ),
            width: 150
        },
        {
            title: 'Sub-Category',
            dataIndex: ['sub_category', 'name'],
            key: 'sub_category',
            render: (text) => text || '-',
            width: 150
        },
        {
            title: 'Status',
            dataIndex: ['status', 'status'],
            key: 'status',
            render: (status) => statusTag(status),
            width: 120
        },
        {
            title: 'Unit',
            dataIndex: ['unit', 'symbol'],
            key: 'unit',
            render: (text) => text || '-',
            width: 100
        },
        {
            title: 'QR Code',
            dataIndex: ['barcode_image', 'name'],
            key: 'barcode_image',
            render: (text, record) => (
                record.barcode_image ? (
                    <Popover
                        content={
                            <img
                                src={`${BACKENDURLFiles}${record.barcode_image}`}
                                alt="QR Code"
                                style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                            />
                        }
                        title="QR Code Preview"
                    >
                        <Avatar
                            src={`${BACKENDURLFiles}${record.barcode_image}`}
                            shape="square"
                            size="large"
                            style={{ cursor: 'pointer' }}
                        />
                    </Popover>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">No QR</Tag>
                )
            ),
            width: 100
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (_, product) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item
                                key="view"
                                icon={<EyeOutlined />}
                                onClick={() => openModal(product.id)}
                            >
                                View Details
                            </Menu.Item>
                            {userPermission?.is_register_product && (
                                <>
                                    <Menu.Item
                                        key="edit"
                                        icon={<EditOutlined />}
                                        onClick={() => showModal(product)}
                                    >
                                        Edit Product
                                    </Menu.Item>
                                    <Menu.Item
                                        key="status"
                                        icon={product.status?.status === 'active' ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                                        onClick={() => handleStatusUpdate(product.id, product.status?.status === 'active' ? 'inactive' : 'active')}
                                    >
                                        {product.status?.status === 'active' ? 'Deactivate' : 'Activate'}
                                    </Menu.Item>
                                    <Menu.Item
                                        key="edit"
                                        icon={<StockOutlined />}
                                        onClick={() => handleBatch(product.id)}
                                    >
                                        Batches
                                    </Menu.Item>
                                    {/* <Menu.Item
                                        key="delete"
                                        icon={<DeleteOutlined />}
                                        danger
                                        onClick={() => {
                                            Modal.confirm({
                                                title: 'Confirm Delete',
                                                content: `Are you sure you want to delete ${product.name}?`,
                                                okText: 'Delete',
                                                okType: 'danger',
                                                onOk: () => handleDelete(product.id)
                                            });
                                        }}
                                    >
                                        Delete
                                    </Menu.Item> */}
                                </>
                            )}
                            {product.barcode_image && (
                                <Menu.Item
                                    key="download"
                                    icon={<DownloadOutlined />}
                                    onClick={() => downloadImage(product.barcode_image, product.name)}
                                >
                                    Download QR
                                </Menu.Item>
                            )}
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', background: '#f0f2f5' }}>
            <Card
                title={
                    <Space>
                        <ShoppingOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                        <Title level={4} style={{ margin: 0 }}>Product Inventory Management</Title>
                    </Space>
                }
                bordered={false}
                style={{
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)',
                    borderRadius: 8
                }}
                bodyStyle={{ padding: 0 }}
                extra={
                    <Space>
                        {userPermission?.is_register_product && (
                            <>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => showModal()}
                                >
                                    New Product
                                </Button>
                                <Button
                                    icon={<ImportOutlined />}
                                    onClick={() => setImportModalVisible(true)}
                                >
                                    Import
                                </Button>
                            </>
                        )}
                        <Dropdown
                            overlay={
                                <Menu>
                                    <Menu.Item
                                        key="excel"
                                        icon={<FileExcelOutlined />}
                                        onClick={exportToExcel}
                                        disabled={exportLoading}
                                    >
                                        Export Excel
                                    </Menu.Item>
                                    <Menu.Item
                                        key="pdf"
                                        icon={<FilePdfOutlined />}
                                        onClick={exportToPDFd}
                                        disabled={exportLoading}
                                    >
                                        Export PDF
                                    </Menu.Item>
                                </Menu>
                            }
                            trigger={['click']}
                        >
                            <Button
                                icon={<ExportOutlined />}
                                loading={exportLoading}
                            >
                                Export
                            </Button>
                        </Dropdown>
                        <Button
                            icon={<QrcodeOutlined />}
                            onClick={qrImageGenrt}
                            loading={qrLoading}
                        >
                            Generate QR
                        </Button>
                    </Space>
                }
            >
                <Card
                    title="Search & Filters"
                    size="small"
                    style={{ marginBottom: 0, borderBottom: '1px solid #f0f0f0' }}
                    headStyle={{
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #f0f0f0'
                    }}
                    bodyStyle={{ padding: '16px 24px' }}
                >
                    <Row gutter={16}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Product Name">
                                <Input
                                    name="name"
                                    placeholder="Search by name"
                                    onChange={handleSearch}
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Category">
                                <Select
                                    name="category_id"
                                    placeholder="Select category"
                                    onChange={handleCategoryChange}
                                    allowClear
                                    suffixIcon={<SettingOutlined />}
                                >
                                    {categories.map((category) => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Sub-Category">
                                <Select
                                    name="sub_category_id"
                                    placeholder="Select sub-category"
                                    onChange={(value) =>
                                        setSearchParams((prevState) => ({
                                            ...prevState,
                                            sub_category_id: value,
                                        }))
                                    }
                                    allowClear
                                    disabled={!searchParams.category_id}
                                    suffixIcon={<SettingOutlined />}
                                >
                                    {subCategories.map((subCategory) => (
                                        <Option key={subCategory.id} value={subCategory.id}>
                                            {subCategory.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item label="Status">
                                <Select
                                    placeholder="Filter by status"
                                    allowClear
                                    onChange={(value) =>
                                        setSearchParams((prevState) => ({
                                            ...prevState,
                                            status: value,
                                        }))
                                    }
                                >
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Table
                    columns={columns}
                    dataSource={Array.isArray(products) ? products : []}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => (
                            <Text strong>Total {total} products</Text>
                        )
                    }}
                    scroll={{ x: 1500 }}
                    loading={loading}
                    size="middle"
                    style={{ padding: '0 24px' }}
                />
            </Card>

            {/* Import Modal */}
            <Modal
                title={
                    <Space>
                        <UploadOutlined style={{ color: '#1890ff' }} />
                        <Text strong>Import Products</Text>
                    </Space>
                }
                visible={importModalVisible}
                onCancel={() => {
                    setImportModalVisible(false);
                    setSelectedFile(null);
                }}
                footer={null}
                width={600}
            >
                <Form layout="vertical">
                    <Form.Item label="Select Excel File">
                        <Button
                            onClick={handleFileClick}
                            block
                            icon={<UploadOutlined />}
                            disabled={loading}
                        >
                            {selectedFile ? selectedFile.name : 'Select File'}
                        </Button>
                        <input
                            id="import-file-input"
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            accept=".xlsx,.xls"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            onClick={handleImport}
                            disabled={!selectedFile || loading}
                            block
                            loading={loading}
                        >
                            Import Products
                        </Button>
                    </Form.Item>

                    <Divider orientation="left" plain>Template</Divider>
                    <Text type="secondary">
                        Download the template file to ensure proper formatting:
                    </Text>
                    <Button
                        type="link"
                        onClick={handleDownloadTemplate}
                        icon={<DownloadOutlined />}
                    >
                        Download Template
                    </Button>
                    <Alert
                        message="Date Format Required"
                        description="The expire_date column must use YYYY-MM-DD format (e.g., 2025-12-31)"
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </Form>
            </Modal>

            <Modal
                title={
                    <Space>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        <Text strong>Confirm Product Import</Text>
                    </Space>
                }
                visible={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                footer={[
                    <Button key="main-only" onClick={() => handleConfirmImport(false)} disabled={confirming}>
                        Import to Main Shop Only
                    </Button>,
                    <Button
                        key="share"
                        type="primary"
                        onClick={() => handleConfirmImport(true)}
                        loading={confirming}
                    >
                        Yes, Import Products
                    </Button>,
                ]}
                width={700}
            >
                <Text>
                    This shop has connected shops that share products. Choose import option:
                </Text>
                <Divider />
                <ul style={{ paddingLeft: 20 }}>
                    {conflictShops.map(shop => (
                        <li key={shop.shop_id}>
                            <Text strong>{shop.shop_name}:</Text> Will create {shop.missing_barcodes.length} new products
                        </li>
                    ))}
                </ul>
            </Modal>

            <Modal
                title={
                    <Space>
                        {editingProduct ? <EditOutlined /> : <PlusOutlined />}
                        <Text strong>{editingProduct ? 'Edit Product' : 'Register New Product'}</Text>
                    </Space>
                }
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                confirmLoading={loading}
                width={700}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                        {editingProduct ? 'Update' : 'Register'}
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ quantity: 0 }}
                    onFinish={handleOk}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Product Name"
                                name="name"
                                rules={[{ required: true, message: 'Please input the product name!' }]}
                            >
                                <Input placeholder="Enter product name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Barcode"
                                name="barcode"
                            >
                                <Input placeholder="Enter barcode (optional)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="SKU"
                                name="sku"
                            >
                                <Input placeholder="Enter SKU (optional)" />
                            </Form.Item>
                        </Col>
                        <Col span={12} />
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Buying Price (Tsh)"
                                name="buying_price"
                                rules={[{ required: true, message: 'Please input the buying price!' }]}
                            >
                                <Input type="number" placeholder="0.00" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Selling Price (Tsh)"
                                name="selling_price"
                                rules={[{ required: true, message: 'Please input the selling price!' }]}
                            >
                                <Input type="number" placeholder="0.00" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Category"
                                name="category_id"
                                rules={[{ required: true, message: 'Please select the category!' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    onChange={handleCategoryChange}
                                    disabled={!!editingProduct}
                                    placeholder="Select category"
                                >
                                    {categories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Sub-Category"
                                name="sub_category_id"
                                rules={[{ required: true, message: 'Please select the sub-category!' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Select sub-category"
                                >
                                    {subCategories.map(subCategory => (
                                        <Option key={subCategory.id} value={subCategory.id}>
                                            {subCategory.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Unit"
                                name="unit_id"
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Select unit (optional)"
                                >
                                    {units.map(unit => (
                                        <Option key={unit.id} value={unit.id}>
                                            {unit.name} ({unit.symbol})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Expire Date"
                                name="expire_date"
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={
                    <Space>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        <Text strong>Product Inventory Details</Text>
                    </Space>
                }
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Table
                    dataSource={selectedProductDetails}
                    columns={[
                        {
                            title: 'Expire Date',
                            dataIndex: 'expire_date',
                            key: 'expire_date',
                            render: (date) => date || 'N/A'
                        },
                        {
                            title: 'Quantity',
                            dataIndex: 'quantity',
                            key: 'quantity',
                            render: (quantity) => <Text strong>{quantity}</Text>
                        },
                    ]}
                    rowKey="id"
                    pagination={false}
                    bordered
                    summary={() => {
                        const totalQuantity = selectedProductDetails.reduce((total, record) => total + record.quantity, 0);
                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>
                                    <Text strong>Total Quantity</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <Text strong type="success">{totalQuantity}</Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}
                />
            </Modal>
        </div>
    );
};

export default Products;